import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  padding: 40px 20px;
  background: #f5f5f5;
`;

const Card = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #5f259f;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  background: white;

  &:focus {
    outline: none;
    border-color: #5f259f;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Button = styled.button`
  padding: 16px 32px;
  background: linear-gradient(135deg, #5f259f 0%, #8e44ad 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(95, 37, 159, 0.4);
  }
`;

const SecondaryButton = styled.button`
  padding: 12px 24px;
  background: #f0f0f0;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    background: #e0e0e0;
  }
`;

const UrlPreview = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const UrlLabel = styled.p`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
`;

const UrlText = styled.code`
  font-size: 12px;
  color: #333;
  word-break: break-all;
  display: block;
  padding: 12px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ddd;
`;

const TestModeNote = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #856404;
  text-align: center;
`;

const QuickAmounts = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const QuickAmountBtn = styled.button`
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: ${props => props.active ? '#5f259f' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: #5f259f;
  }
`;

const PaymentDemo = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '599',
    orderId: `TEST_${Date.now().toString(36).toUpperCase()}`,
    appName: 'WillovateResto',
    description: 'Food Order Payment',
    customerName: 'John Doe',
    customerPhone: '9876543210',
    customerEmail: 'john@example.com'
  });

  const [openMethod, setOpenMethod] = useState('redirect');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const generatePaymentUrl = () => {
    const params = new URLSearchParams({
      amount: formData.amount,
      orderId: formData.orderId,
      appName: formData.appName,
      description: formData.description,
      callbackUrl: `${window.location.origin}/payment-demo`,
      customerName: formData.customerName,
      customerPhone: formData.customerPhone,
      customerEmail: formData.customerEmail
    });

    return `/payment?${params.toString()}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const paymentUrl = generatePaymentUrl();

    if (openMethod === 'popup') {
      const width = 500;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      const popup = window.open(
        paymentUrl,
        'WillovatePayment',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // Listen for payment completion
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'WILLOVATE_PAYMENT_COMPLETE') {
          window.removeEventListener('message', handleMessage);
          alert(`Payment ${event.data.status}!\nTransaction ID: ${event.data.transactionId || 'N/A'}\nOrder ID: ${event.data.orderId}`);
        }
      };
      window.addEventListener('message', handleMessage);
    } else {
      navigate(paymentUrl);
    }
  };

  const regenerateOrderId = () => {
    setFormData({
      ...formData,
      orderId: `TEST_${Date.now().toString(36).toUpperCase()}`
    });
  };

  const quickAmounts = [100, 299, 599, 999, 1999, 4999];

  return (
    <Container>
      <Card>
        <Title>🧪 Payment Demo</Title>
        <Subtitle>Test how the payment window looks and works</Subtitle>

        <TestModeNote>
          ⚠️ This is a demo page to test the payment UI. No actual payments will be processed.
        </TestModeNote>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Amount (₹)</Label>
            <Input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              min="1"
              required
            />
            <QuickAmounts>
              {quickAmounts.map(amt => (
                <QuickAmountBtn
                  key={amt}
                  type="button"
                  active={formData.amount === amt.toString()}
                  onClick={() => setFormData({ ...formData, amount: amt.toString() })}
                >
                  ₹{amt}
                </QuickAmountBtn>
              ))}
            </QuickAmounts>
          </FormGroup>

          <Row>
            <FormGroup>
              <Label>Order ID</Label>
              <Input
                type="text"
                name="orderId"
                value={formData.orderId}
                onChange={handleChange}
                required
              />
              <SecondaryButton type="button" onClick={regenerateOrderId}>
                🔄 Regenerate
              </SecondaryButton>
            </FormGroup>

            <FormGroup>
              <Label>App Name</Label>
              <Select
                name="appName"
                value={formData.appName}
                onChange={handleChange}
              >
                <option value="WillovateResto">WillovateResto</option>
                <option value="WillovateShop">WillovateShop</option>
                <option value="WillovateEdu">WillovateEdu</option>
                <option value="TestApp">TestApp</option>
              </Select>
            </FormGroup>
          </Row>

          <FormGroup>
            <Label>Description</Label>
            <Input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </FormGroup>

          <Row>
            <FormGroup>
              <Label>Customer Name</Label>
              <Input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Customer Phone</Label>
              <Input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleChange}
              />
            </FormGroup>
          </Row>

          <FormGroup>
            <Label>Customer Email</Label>
            <Input
              type="email"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Open Method</Label>
            <Select
              name="openMethod"
              value={openMethod}
              onChange={(e) => setOpenMethod(e.target.value)}
            >
              <option value="redirect">Full Page Redirect</option>
              <option value="popup">Popup Window</option>
            </Select>
          </FormGroup>

          <Button type="submit">
            🚀 Open Payment Window
          </Button>
        </Form>

        <UrlPreview>
          <UrlLabel>Generated Payment URL:</UrlLabel>
          <UrlText>{window.location.origin}{generatePaymentUrl()}</UrlText>
        </UrlPreview>
      </Card>
    </Container>
  );
};

export default PaymentDemo;
